import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Badge } from './ui/badge';
import { AlertTriangle, Loader2, Receipt, FileMinus2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(n || 0));

/**
 * Modale Avoir FNE
 *
 * Permet d'émettre un avoir (refund) sur une facture certifiée FNE.
 * - Refund total : tout le reliquat (auto-calculé côté backend si items=null)
 * - Refund partiel : sélection par ligne avec quantité ≤ reliquat
 *
 * Props :
 *   open, onClose, onSuccess(refundResult)
 *   targetType : "spot" | "contract"
 *   targetId   : id de l'opération (spot) ou de l'invoice (contract)
 *   targetLabel: ex. "FACT-2026-0067" (pour le header)
 */
export default function FneRefundModal({
  open,
  onClose,
  onSuccess,
  targetType,
  targetId,
  targetLabel,
}) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('total'); // 'total' | 'partial'
  const [quantities, setQuantities] = useState({}); // fneItemId -> qty
  const [selected, setSelected] = useState({}); // fneItemId -> bool
  const [reason, setReason] = useState('');
  const [successResult, setSuccessResult] = useState(null);

  /* -------------------- Load context -------------------- */
  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/fne/refund-context/${targetId}`, {
        params: { targetType },
      });
      setCtx(data);
      const initQty = {};
      const initSel = {};
      (data.items || []).forEach((it) => {
        initQty[it.fneItemId] = it.remainingQuantity;
        initSel[it.fneItemId] = false; // aucune ligne sélectionnée par défaut en partiel
      });
      setQuantities(initQty);
      setSelected(initSel);
    } catch (err) {
      toast.error('Erreur chargement contexte avoir', {
        description: err?.response?.data?.detail || err.message,
      });
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType, onClose]);

  useEffect(() => {
    if (!open || !targetId) return;
    loadContext();
  }, [open, targetId, loadContext]);

  /* -------------------- Reset state on close -------------------- */
  const resetForm = useCallback(() => {
    setCtx(null);
    setMode('total');
    setQuantities({});
    setSelected({});
    setReason('');
    setSubmitting(false);
    setSuccessResult(null);
  }, []);

  useEffect(() => {
    if (!open) resetForm();
  }, [open, resetForm]);

  /* -------------------- Helpers selection -------------------- */
  const allItems = ctx?.items || [];
  const refundableItems = allItems.filter((it) => it.remainingQuantity > 0);
  const allSelected = refundableItems.length > 0 && refundableItems.every((it) => selected[it.fneItemId]);
  const someSelected = refundableItems.some((it) => selected[it.fneItemId]);

  const toggleAll = () => {
    const newVal = !allSelected;
    const next = { ...selected };
    refundableItems.forEach((it) => {
      next[it.fneItemId] = newVal;
    });
    setSelected(next);
  };

  const toggleOne = (fneItemId) => {
    setSelected((s) => ({ ...s, [fneItemId]: !s[fneItemId] }));
  };

  /* -------------------- Computed totals -------------------- */
  const partialTotal = useMemo(() => {
    if (!ctx) return 0;
    return (ctx.items || []).reduce((acc, it) => {
      if (!selected[it.fneItemId]) return acc;
      const q = parseFloat(quantities[it.fneItemId] || 0);
      if (!q || q <= 0) return acc;
      return acc + q * (it.unitAmount || 0);
    }, 0);
  }, [ctx, quantities, selected]);

  const totalAmount = mode === 'partial' ? partialTotal : (ctx?.totalRemainingAmount || 0);

  /* -------------------- Validation -------------------- */
  const partialErrors = useMemo(() => {
    if (mode !== 'partial' || !ctx) return [];
    const errs = [];
    let hasOne = false;
    (ctx.items || []).forEach((it) => {
      if (!selected[it.fneItemId]) return;
      const q = parseFloat(quantities[it.fneItemId] || 0);
      if (q > 0) hasOne = true;
      if (q > it.remainingQuantity + 1e-6) {
        errs.push(`${it.description || it.fneItemId} : ${q} > reliquat ${it.remainingQuantity}`);
      }
      if (q < 0) {
        errs.push(`${it.description || it.fneItemId} : quantité négative`);
      }
    });
    if (!hasOne) errs.push('Cochez au moins une ligne à rembourser (quantité > 0).');
    return errs;
  }, [mode, ctx, quantities, selected]);

  const canSubmit = !submitting && reason.trim().length > 0 && partialErrors.length === 0;

  /* -------------------- Submit refund -------------------- */
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        targetType,
        reason: reason.trim(),
      };
      if (mode === 'partial') {
        body.items = (ctx.items || [])
          .map((it) => {
            if (!selected[it.fneItemId]) return null;
            const q = parseFloat(quantities[it.fneItemId] || 0);
            if (!q || q <= 0) return null;
            return { id: it.fneItemId, quantity: q };
          })
          .filter(Boolean);
      }
      const { data } = await axios.post(`${API}/fne/refund/${targetId}`, body);
      toast.success('Avoir FNE émis avec succès', {
        description: data?.creditNoteNumber
          ? `Avoir : ${data.creditNoteNumber}`
          : `Référence DGI : ${data?.reference || 'N/A'}`,
      });
      setSuccessResult(data);
      // ne pas fermer immédiatement → laisse le user télécharger
    } catch (err) {
      toast.error("Échec de l'émission de l'avoir", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* -------------------- Render -------------------- */
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="fne-refund-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <FileMinus2 className="w-5 h-5" />
            Émettre un avoir FNE — {targetLabel || targetId}
          </DialogTitle>
        </DialogHeader>

        {successResult ? (
          <div className="py-4 space-y-4">
            <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="flex-1 text-sm">
                <div className="font-semibold text-emerald-800 mb-1">Avoir FNE certifié ✅</div>
                {successResult.creditNoteNumber && (
                  <div className="text-emerald-900">
                    Note de crédit générée :{' '}
                    <span className="font-mono font-semibold">{successResult.creditNoteNumber}</span>
                  </div>
                )}
                <div className="text-xs text-emerald-700 mt-1">
                  Le PDF a été archivé. Un email a été envoyé au client si le SMTP est configuré.
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {successResult.creditNoteNumber && (
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => {
                    const url = `${API}/documents/download/${successResult.creditNoteNumber}`;
                    window.open(url, '_blank');
                  }}
                  data-testid="fne-refund-download-btn"
                >
                  <FileMinus2 className="w-4 h-4 mr-2" />
                  Télécharger l&apos;avoir PDF
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  onSuccess?.(successResult);
                }}
                data-testid="fne-refund-close-btn"
              >
                Fermer
              </Button>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Chargement du contexte…
          </div>
        ) : !ctx ? (
          <div className="text-sm text-rose-700">Impossible de charger les informations.</div>
        ) : !ctx.canRefund ? (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Avoir non disponible</div>
              <div className="text-xs mt-1">
                Statut actuel : <Badge variant="outline">{ctx.status || 'inconnu'}</Badge>.
                Seules les factures <code className="font-mono">certified</code> ou{' '}
                <code className="font-mono">refunded_partial</code> peuvent faire l&apos;objet d&apos;un avoir.
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Bandeau info */}
            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded">
              <div className="text-xs">
                <div className="text-slate-500">Référence DGI</div>
                <div className="font-mono text-slate-800 font-semibold">{ctx.reference || '—'}</div>
              </div>
              <div className="text-xs text-right">
                <div className="text-slate-500">Statut</div>
                <Badge
                  variant="outline"
                  className={
                    ctx.status === 'refunded_partial'
                      ? 'border-amber-300 text-amber-700 bg-amber-50'
                      : 'border-emerald-300 text-emerald-700 bg-emerald-50'
                  }
                  data-testid="fne-refund-status-badge"
                >
                  {ctx.status}
                </Badge>
              </div>
            </div>

            {/* Choix mode */}
            <div>
              <Label className="text-sm font-semibold">Type d&apos;avoir</Label>
              <RadioGroup
                value={mode}
                onValueChange={setMode}
                className="mt-2 grid grid-cols-2 gap-2"
                data-testid="fne-refund-mode"
              >
                <label
                  className={`flex items-center gap-2 p-3 border rounded cursor-pointer transition-colors ${
                    mode === 'total'
                      ? 'border-rose-400 bg-rose-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <RadioGroupItem value="total" data-testid="fne-refund-mode-total" />
                  <div>
                    <div className="text-sm font-medium">Annulation totale</div>
                    <div className="text-xs text-slate-500">Tout le reliquat</div>
                  </div>
                </label>
                <label
                  className={`flex items-center gap-2 p-3 border rounded cursor-pointer transition-colors ${
                    mode === 'partial'
                      ? 'border-rose-400 bg-rose-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <RadioGroupItem value="partial" data-testid="fne-refund-mode-partial" />
                  <div>
                    <div className="text-sm font-medium">Avoir partiel</div>
                    <div className="text-xs text-slate-500">Sélectionner les lignes</div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Tableau des lignes */}
            <div className="border border-slate-200 rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="text-slate-600">
                    {mode === 'partial' && (
                      <th className="text-center p-2 font-medium w-10">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
                          onChange={toggleAll}
                          className="cursor-pointer accent-rose-600"
                          title={allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                          data-testid="fne-refund-select-all"
                        />
                      </th>
                    )}
                    <th className="text-left p-2 font-medium">Désignation</th>
                    <th className="text-right p-2 font-medium">PU</th>
                    <th className="text-right p-2 font-medium">Initial</th>
                    <th className="text-right p-2 font-medium">Déjà avoir</th>
                    <th className="text-right p-2 font-medium">Reliquat</th>
                    {mode === 'partial' && (
                      <th className="text-right p-2 font-medium w-24">À rembourser</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(ctx.items || []).map((it) => {
                    const isSelected = !!selected[it.fneItemId];
                    const disabled = it.remainingQuantity <= 0;
                    return (
                    <tr
                      key={it.fneItemId}
                      className={`border-t border-slate-100 transition-colors ${
                        mode === 'partial' && isSelected ? 'bg-rose-50/60' : ''
                      } ${disabled ? 'opacity-50' : 'hover:bg-slate-50/60'}`}
                    >
                      {mode === 'partial' && (
                        <td className="p-2 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOne(it.fneItemId)}
                            disabled={disabled}
                            className="cursor-pointer accent-rose-600"
                            data-testid={`fne-refund-select-${it.fneItemId}`}
                          />
                        </td>
                      )}
                      <td className="p-2">
                        <div className="text-slate-800 line-clamp-1">
                          {it.description || <span className="italic text-slate-400">—</span>}
                        </div>
                        <div className="text-[10px] font-mono text-slate-400">{it.fneItemId}</div>
                      </td>
                      <td className="p-2 text-right tabular-nums">{fmtMoney(it.unitAmount)}</td>
                      <td className="p-2 text-right tabular-nums">{it.initialQuantity}</td>
                      <td className="p-2 text-right tabular-nums text-amber-700">
                        {it.alreadyRefunded || 0}
                      </td>
                      <td className="p-2 text-right tabular-nums font-medium text-emerald-700">
                        {it.remainingQuantity}
                      </td>
                      {mode === 'partial' && (
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            max={it.remainingQuantity}
                            value={isSelected ? (quantities[it.fneItemId] ?? '') : ''}
                            onChange={(e) =>
                              setQuantities((q) => ({
                                ...q,
                                [it.fneItemId]: e.target.value,
                              }))
                            }
                            disabled={!isSelected || disabled}
                            placeholder={isSelected ? '' : '—'}
                            className="h-8 text-right tabular-nums"
                            data-testid={`fne-refund-qty-${it.fneItemId}`}
                          />
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="bg-rose-50 border-t border-rose-200 p-2 flex justify-between items-center">
                <span className="text-xs text-rose-700 font-medium">
                  <Receipt className="w-3 h-3 inline mr-1" />
                  Montant total avoir
                </span>
                <span
                  className="text-sm font-bold text-rose-700 tabular-nums"
                  data-testid="fne-refund-total"
                >
                  {fmtMoney(totalAmount)} F CFA
                </span>
              </div>
            </div>

            {/* Erreurs de validation partielle */}
            {partialErrors.length > 0 && (
              <ul className="text-xs text-rose-700 space-y-1 bg-rose-50 border border-rose-200 rounded p-2">
                {partialErrors.map((e, i) => (
                  <li key={i}>• {e}</li>
                ))}
              </ul>
            )}

            {/* Motif obligatoire */}
            <div>
              <Label htmlFor="refund-reason" className="text-sm font-semibold">
                Motif de l&apos;avoir <span className="text-rose-600">*</span>
              </Label>
              <Textarea
                id="refund-reason"
                placeholder="Ex : Erreur de facturation, annulation client, livraison non conforme…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="mt-1 text-sm"
                data-testid="fne-refund-reason"
              />
            </div>
          </div>
        )}

        {!successResult && (
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            data-testid="fne-refund-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || !ctx?.canRefund}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="fne-refund-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Émission en cours…
              </>
            ) : (
              <>
                <FileMinus2 className="w-4 h-4 mr-2" />
                Émettre l&apos;avoir
              </>
            )}
          </Button>
        </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
